import { getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import "../../../utils/pdfWorker";

/**
 * Vorschaubilder für den Seiten-Organisator.
 *
 * Eine Quelle wird einmal geöffnet; Seiten werden erst auf Anfrage gerendert
 * (die Kacheln fragen per IntersectionObserver, sobald sie sichtbar werden)
 * und je Index gecacht. Eine Drehung zeigt die Kachel per CSS — dafür wird
 * nicht neu gerendert.
 *
 * Ohne Unit-Test: Canvas und PDF.js-Worker laufen in jsdom nicht. Die UI-Tests
 * mocken dieses Modul.
 */
export interface VorschauQuelle {
    /** Data-URL der Seite `index` (0-basiert), `breitePx` CSS-Pixel breit. */
    seite(index: number, breitePx: number): Promise<string>;
    schliessen(): void;
}

export function erzeugeVorschauQuelle(bytes: Uint8Array): VorschauQuelle {
    // Kopie! PDF.js überträgt den Puffer an den Worker und macht ihn hier
    // unbrauchbar — die Bytes braucht der Organisator aber noch zum Bauen.
    const task = getDocument({ data: bytes.slice(), isEvalSupported: false });
    const cache = new Map<string, Promise<string>>();
    let geschlossen = false;

    const rendere = async (index: number, breitePx: number): Promise<string> => {
        const pdf: PDFDocumentProxy = await task.promise;
        const page = await pdf.getPage(index + 1);
        const scale = breitePx / page.getViewport({ scale: 1 }).width;
        // Schärfe auf hochauflösenden Bildschirmen: in Gerätepixeln rendern.
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const viewport = page.getViewport({ scale: scale * dpr });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        // PDF.js 5: `canvas` statt des veralteten `canvasContext`.
        await page.render({ canvas, viewport }).promise;
        page.cleanup();
        if (geschlossen) throw new Error("Vorschau geschlossen.");
        return canvas.toDataURL("image/png");
    };

    return {
        seite(index, breitePx) {
            const key = `${index}@${breitePx}`;
            let p = cache.get(key);
            if (!p) {
                p = rendere(index, breitePx);
                // Fehlschläge nicht cachen — die Kachel darf es erneut versuchen.
                p.catch(() => cache.delete(key));
                cache.set(key, p);
            }
            return p;
        },
        schliessen() {
            geschlossen = true;
            cache.clear();
            void task.destroy();
        },
    };
}
